export var PerfTag;
(function (PerfTag) {
    PerfTag["appExpectedMemoryUsageLimit"] = "APP_EXPECTED_MEMORY_USAGE_LIMIT";
    PerfTag["appMemoryExpectedTotalCommitLimit"] = "APP_MEMORY_EXPECTED_TOTAL_COMMIT_LIMIT";
    PerfTag["appMemoryPeakPrivateCommitUsage"] = "APP_MEMORY_PEAK_PRIVATE_COMMIT_USAGE";
    PerfTag["appMemoryPrivateCommitUsage"] = "APP_MEMORY_PRIVATE_COMMIT_USAGE";
    PerfTag["appMemoryTotalCommitLimit"] = "APP_MEMORY_TOTAL_COMMIT_LIMIT";
    PerfTag["appMemoryTotalCommitUsage"] = "APP_MEMORY_TOTAL_COMMIT_USAGE";
    PerfTag["appMemoryUsage"] = "APP_MEMORY_USAGE";
    PerfTag["appMemoryUsageLevel"] = "APP_MEMORY_USAGE_LEVEL";
    PerfTag["appMemoryUsageLimit"] = "APP_MEMORY_USAGE_LIMIT";
    PerfTag["availableSystemMemory"] = "AVAILABLE_SYSTEM_MEMORY";
    PerfTag["cpuUsage"] = "CPU_USAGE";
    PerfTag["fps"] = "FPS";
    PerfTag["memory"] = "MEMORY";
    PerfTag["memoryLimit"] = "MEMORY_LIMIT";
    PerfTag["processMemoryPrivateWorkingSetUsage"] = "PROCESS_MEMORY_PRIVATE_WORKING_SET_USAGE";
    PerfTag["processMemoryTotalWorkingSetUsage"] = "PROCESS_MEMORY_TOTAL_WORKING_SET_USAGE";
    PerfTag["systemCommittedMemory"] = "SYSTEM_COMMITTED_MEMORY";
    PerfTag["totalSystemMemory"] = "TOTAL_SYSTEM_MEMORY";
    PerfTag["userAgentSpecificMemory"] = "USER_AGENT_SPECIFIC_MEMORY";
    // TODO These tags are only used by tests, rewrite tests so we can delete these https://gspcloud.atlassian.net/browse/LTV-15388
    // eslint-disable-next-line typescript-sort-keys/string-enum
    PerfTag["advertisingReady"] = "ADVERTISING_READY";
    PerfTag["rebufferEnd"] = "REBUFFER_END";
    PerfTag["rebufferStart"] = "REBUFFER_START";
    PerfTag["seekEnd"] = "SEEK_END";
    PerfTag["seekStart"] = "SEEK_START";
    PerfTag["sessionStart"] = "SESSION_START";
})(PerfTag || (PerfTag = {}));
export var PerfKey;
(function (PerfKey) {
    PerfKey["playerState"] = "PLAYER_STATE";
    PerfKey["render"] = "RDR";
    PerfKey["root"] = "ROOT";
    PerfKey["telemetry"] = "TELEMETRY";
})(PerfKey || (PerfKey = {}));
const MEASUREMENTS_STORE_SOFT_LIMIT = 1000; // Prevent more than X entries in the memory store to help prevent memory leaks
const ENTRY_LIMIT_TOLERANCE = 100; // Allow Y more entries than the soft limit to prevent eviction on each new entry and instead allow batch eviction
export class PerformanceLog {
    handlers = [];
    registerHandler(handler) {
        this.handlers.push(handler);
    }
    measure(key, context, value) {
        this.handlers.forEach((handler) => {
            handler(key, context, value);
        });
    }
}
function time() {
    // This check is really necessary because old devices don't have `self.performance`.
    // https://developer.mozilla.org/en-US/docs/Web/API/Performance#browser_compatibility
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (self.performance) {
        return self.performance.now();
    }
    return Date.now();
}
/**
 * @public
 */
export function createMeasurementsStore(softEntryLimit = MEASUREMENTS_STORE_SOFT_LIMIT) {
    const perflogs = [];
    let contexts = {};
    function createContext(key) {
        const context = {
            baseTime: time(),
            lastTime: 0,
        };
        contexts[key] = context;
        return context;
    }
    function getContext(key) {
        if (key in contexts) {
            return contexts[key];
        }
        return createContext(key);
    }
    function perfTime(contextKey) {
        const context = getContext(contextKey);
        const currentTime = time() - context.baseTime;
        const deltaTime = currentTime - context.lastTime;
        context.lastTime = currentTime;
        return { currentTime, deltaTime };
    }
    function getLastTagTime(tag, key, deltaTime = false) {
        let finalKey = key;
        if (!finalKey) {
            finalKey = "ROOT" /* PerfKey.root */;
        }
        const entry = getMatchingEntries(finalKey, tag).pop();
        return extractTime(entry, deltaTime);
    }
    function getFirstTagTime(tag, key, deltaTime = false) {
        let finalKey = key;
        if (!finalKey) {
            finalKey = "ROOT" /* PerfKey.root */;
        }
        const entry = getMatchingEntries(finalKey, tag)[0];
        return extractTime(entry, deltaTime);
    }
    function getFirstTagTimeDiff(lateTag, earlyTag, key = "ROOT" /* PerfKey.root */) {
        const lateTagTime = getFirstTagTime(lateTag, key);
        const earlyTagTime = getFirstTagTime(earlyTag, key);
        if (lateTagTime !== undefined && earlyTagTime !== undefined) {
            return lateTagTime - earlyTagTime;
        }
        return undefined;
    }
    function getMatchingEntries(key, tag) {
        return perflogs.filter((log) => log.key === key && log.tag === tag);
    }
    function extractTime(entry, deltaTime) {
        if (entry) {
            if (deltaTime) {
                return entry.t.deltaTime;
            }
            return entry.t.currentTime;
        }
        else {
            return undefined;
        }
    }
    function getTagTimeDiff(lateTag, earlyTag, key = "ROOT" /* PerfKey.root */) {
        const lateTagTime = getLastTagTime(lateTag, key);
        const earlyTagTime = getLastTagTime(earlyTag, key);
        if (lateTagTime !== undefined && earlyTagTime !== undefined) {
            return lateTagTime - earlyTagTime;
        }
        return undefined;
    }
    function reset() {
        contexts = {};
        perflogs.splice(0, perflogs.length);
    }
    function metricsHandler(key, tag, value) {
        const t = perfTime(key);
        perflogs.push(value !== undefined ? { key, tag, t, value } : { key, tag, t });
        if (perflogs.length > softEntryLimit + ENTRY_LIMIT_TOLERANCE) {
            perflogs.splice(0, ENTRY_LIMIT_TOLERANCE);
        }
    }
    return {
        metricsHandler,
        reset,
        perflogs,
        getLastTagTime,
        getFirstTagTime,
        getTagTimeDiff,
        getFirstTagTimeDiff,
    };
}
/**
 * @public
 */
export function startRenderTimeMonitoring(performanceLog, intervalMs = 1000, perfKey = "TELEMETRY" /* PerfKey.telemetry */) {
    let stopped = false;
    let renderTimes = [];
    function captureRenderTime() {
        renderTimes.push(time());
        if (!stopped) {
            requestAnimationFrame(captureRenderTime);
        }
    }
    requestAnimationFrame(captureRenderTime);
    const handle = setInterval(() => {
        const now = time();
        const framesPerInterval = renderTimes.filter((t) => t >= now - intervalMs).length;
        const intervalSeconds = intervalMs / 1000;
        const fps = framesPerInterval / intervalSeconds;
        performanceLog.measure(perfKey, "FPS" /* PerfTag.fps */, fps);
        renderTimes = [];
    }, intervalMs);
    return () => {
        stopped = true;
        clearInterval(handle);
    };
}
//# sourceMappingURL=perf.js.map